import React, { useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { useTheme } from 'react-jss'
import { Root, Overlay, Content, Portal } from '@radix-ui/react-dialog'
import { Button } from '../../../ui/Button/Button'
import { useStyles } from '../../../ui/Dialog/Dialog'
import { getRejectCaseReasonList } from '../../../../api/salary'
import { colors } from '../../../../theme/palette'
import { IkonPromptsucces } from '../../../../assets/icons'
import { Case } from '../../../../models/Case'
import { L } from '../../../providers/Lokalise'
import { RejectReason } from '../../../../models/RejectReason'
import { CaseDetail } from '../../../../models/CaseDetail'
import { Typography } from '../../../ui/Typography/Typography'
import { formatDate } from '../../../../utils/format'
import { TakeCaseContentView } from '../TakeCase'
import { RejectCaseView } from '../RejectCase'
import { AttachToAnotherCase } from '../AttachToAnotherCase'
import Close from '../../../ui/Dialog/Close'
import Title from '../../../ui/Dialog/Title'
import {
    fetchCasesAtom,
    openCasesAtom,
} from '../../../../store/filter/filterCasesAtom'

interface CaseDetailsPromptProps {
    caseDetails?: CaseDetail
    caseId: string
    caseRegistrationDate: string
    onSuccess?: () => void
    // handleClose?: () => void
    open?: boolean
}

export const CaseDetailsPrompt: React.FC<CaseDetailsPromptProps> = ({
    caseDetails,
    caseId,
    caseRegistrationDate,
    onSuccess,
    open,
}) => {
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white
    const [showRejectCase, setShowRejectCase] = useState(false)
    const [showInitialView, setShowInitialView] = useState(true)
    const [showTakeCase, setShowTakeCase] = useState(false)
    const [openCases] = useAtom(openCasesAtom)
    const [showAttachToAnotherCase, setShowAttachToAnotherCase] =
        useState(false)

    const [rejectCaseReasonList, setRejectCaseReasonList] = useState<
        RejectReason[]
    >([])
    const [, fetchCases] = useAtom(fetchCasesAtom)

    useEffect(() => {
        const fetchRejectCaseReasonList = async (): Promise<void> => {
            const response = await getRejectCaseReasonList()
            const mapped = response.map((item) => RejectReason.build(item))
            setRejectCaseReasonList(mapped)
        }

        if (open && rejectCaseReasonList.length === 0) {
            fetchRejectCaseReasonList()
        }
    }, [open, getRejectCaseReasonList, rejectCaseReasonList, openCases])

    useEffect(() => {
        if (openCases && openCases.length === 0) {
            fetchCases()
        }
    }, [openCases])

    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const initialView = (): JSX.Element => {
        return (
            <div className={`${classes.container} withIcon`}>
                <div className={classes.circleIcon}>
                    <IkonPromptsucces width="46px" fill={colors.green} />
                </div>
                <Close />
                <Title
                    style={{ marginBottom: '30px' }}
                    title={L('LØN_SAG_DETALJER_PROMPT_TITLE')}
                />
                <div
                    style={{
                        width: '100%',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        tag="p"
                        size="XS"
                        align="center"
                        weight="bold"
                        color="black"
                        style={{ marginBottom: '10px' }}
                    >
                        {caseDetails?.newlyHired?.name}
                    </Typography>
                    <Typography
                        tag="p"
                        size="XXS"
                        align="center"
                        color="black"
                        style={{ marginBottom: '10px' }}
                    >
                        Sagsnr: {caseDetails?.caseNumber}
                    </Typography>
                    <Typography
                        tag="p"
                        size="XXS"
                        align="center"
                        color="black"
                        style={{ marginBottom: '10px' }}
                    >
                        Frist på sag:{' '}
                        {formatDate(new Date(caseDetails?.deadlineDate))}
                    </Typography>
                    <Typography
                        tag="p"
                        size="XXS"
                        align="center"
                        color="black"
                        style={{ marginBottom: '10px' }}
                    >
                        Arbejdssted: {caseDetails?.newlyHired?.workplace?.navn}
                    </Typography>
                    <Typography
                        tag="p"
                        size="XXS"
                        align="center"
                        color="black"
                        style={{ marginBottom: '10px' }}
                    >
                        Stilling: {caseDetails?.newlyHired?.position?.navn}
                    </Typography>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            marginTop: '2rem',
                        }}
                    >
                        <Button
                            type="primary"
                            size="large"
                            disabled={
                                !caseDetails?.allowedUpdateActionList?.includes(
                                    'Accept'
                                )
                            }
                            onClick={() => {
                                setShowInitialView(false)
                                setShowTakeCase(true)
                            }}
                        >
                            {L('LØN_SAG_DETALJER_PROMPT_TAG_SAG_KNAP')}
                        </Button>
                        <Button
                            type="secondary"
                            size="large"
                            disabled={
                                !caseDetails?.allowedUpdateActionList?.includes(
                                    'SammenfletMedAndenSag'
                                )
                            }
                            onClick={() => {
                                setShowInitialView(false)
                                setShowAttachToAnotherCase(true)
                            }}
                        >
                            {/* Jeg har allerede tager sagen (sammenlæg) */}
                            {L('LØN_SAG_DETALJER_PROMPT_SAMMENLÆG_KNAP')}
                        </Button>
                        <Button
                            type="secondary"
                            size="large"
                            disabled={
                                !caseDetails?.allowedUpdateActionList?.includes(
                                    'Afvis'
                                )
                            }
                            onClick={() => {
                                setShowInitialView(false)
                                setShowRejectCase(true)
                            }}
                        >
                            {/* Afvis sagen */}
                            {L('LØN_SAG_DETALJER_PROMPT_AFVIS_SAG_KNAP')}
                        </Button>
                    </div>
                </div>
                <Close />
            </div>
        )
    }

    return (
        <Root defaultOpen>
            <Portal>
                <Overlay className={classes.overlay} />
                <Content className={`${classes.overlayContainer} withIcon`}>
                    {showInitialView && initialView()}
                    {showRejectCase && (
                        <RejectCaseView
                            caseId={caseId}
                            caseRegistrationDate={caseRegistrationDate}
                            onSuccess={() => {
                                onSuccess?.()
                                setShowRejectCase(false)
                            }}
                            onCancel={() => {
                                setShowInitialView(true)
                                setShowRejectCase(false)
                            }}
                            navigateToCaseList
                        />
                    )}
                    {showAttachToAnotherCase && (
                        <AttachToAnotherCase
                            currentCase={Case.buildCaseFromCaseDetails(
                                caseDetails
                            )}
                            onSuccess={() => {
                                onSuccess?.()
                                setShowAttachToAnotherCase(false)
                            }}
                            onCancel={() => {
                                setShowInitialView(true)
                                setShowAttachToAnotherCase(false)
                            }}
                            openCases={openCases}
                        />
                    )}
                    {showTakeCase && (
                        <TakeCaseContentView
                            currentCase={Case.buildCaseFromCaseDetails(
                                caseDetails
                            )}
                            isOpen={showTakeCase}
                            handleClose={() => {
                                setShowTakeCase(false)
                            }}
                            onSuccess={() => {
                                onSuccess?.()
                                setShowTakeCase(false)
                            }}
                        />
                    )}
                </Content>
            </Portal>
        </Root>
    )
}
